import { Button, Form, Input, Modal, Select, Upload, message } from "antd";
import { useUnit } from "effector-react";
import {
  $chosenUser,
  changeChosenUserEv,
  sendEditUserFx,
} from "../../models/userListModel";
import { useEffect, useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import style from "./ClientUserList.module.css";

const { Option } = Select;
const selectOptions = [
  { label: "Сотрудник", value: "Сотрудник" },
  { label: "Уволен", value: "Уволен" },
  { label: "Потенциальный сотрудник", value: "Потенциальный сотрудник" },
];

export default function UserListModal() {
  const [form] = Form.useForm();
  const chosenUser = useUnit($chosenUser);
  const changeChosenUser = useUnit(changeChosenUserEv);
  const sendEditUser = useUnit(sendEditUserFx);
  const pending = useUnit(sendEditUserFx.pending);

  const [uploadedPhotoBase64, setUploadedPhotoBase64] = useState(null);

  const handlePhotoUpload = ({ fileList }) => {
    if (fileList.length > 0) {
      const file = fileList[0].originFileObj || fileList[0];

      // Reject images larger than 300KB
      if (file.size > 300 * 1024) {
        message.error("Файл слишком большой! Максимальный размер: 300KB.");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        let base64 = e.target.result;
        setUploadedPhotoBase64(base64);
        message.success("Фото успешно загружено!");
      };
      reader.readAsDataURL(file);
    } else {
      setUploadedPhotoBase64(null);
      message.info("Фото удалено.");
    }
  };

  const onFinish = (values) => {
    if (!chosenUser || (!chosenUser.user_id && !chosenUser.partition_key)) {
      message.error(
        "⚠️ Ошибка: отсутствуют обязательные данные пользователя (user_id)."
      );
      console.warn("🚨 Missing user_id:", chosenUser);
      return;
    }

    // Use sort_key as place_id if place_id is null
    const placeId = chosenUser.place_id || chosenUser.sort_key;

    if (!placeId) {
      message.error("⚠️ Ошибка: отсутствует place_id.");
      console.warn("🚨 Missing place_id:", chosenUser);
      return;
    }

    // Use existing presigned_url if no new image is uploaded
    const existingImageBase64 = chosenUser.presigned_url
      ? [chosenUser.presigned_url] // Ensure this is an array
      : [];

    const payload = {
      action: "update",
      values: {
        user_id: chosenUser.user_id || chosenUser.partition_key, // Ensure this is correct
        place_id: placeId,
        emp_department:
          values.emp_department || chosenUser.emp_department || "", // Default to empty string if missing
        emp_position: values.emp_position || chosenUser.emp_position || "",
        start_work_time:
          values.start_work_time || chosenUser.start_work_time || "",
        end_work_time: values.end_work_time || chosenUser.end_work_time || "",
        user_name: values.user_name || chosenUser.user_name || "",
        user_surname: values.user_surname || chosenUser.user_surname || "",
        emp_category: values.emp_category || chosenUser.emp_category || "",
        user_gender: values.user_gender || chosenUser.user_gender || "",
        data_url: uploadedPhotoBase64
          ? [uploadedPhotoBase64] // Ensure this is an array
          : existingImageBase64,
      },
    };

    // ✅ Log the payload before sending
    console.log("📤 FINAL PAYLOAD:", JSON.stringify(payload, null, 2));

    sendEditUser(payload);
  };

  const onClose = () => {
    changeChosenUser(null);
    setUploadedPhotoBase64(null);
  };

  useEffect(() => {
    console.log("📌 chosenUser:", chosenUser);
    if (chosenUser) {
      form.setFieldsValue({
        user_name: chosenUser?.user_name,
        user_surname: chosenUser?.user_surname,
        user_gender: chosenUser?.user_gender,
        emp_department: chosenUser?.emp_department,
        emp_position: chosenUser?.emp_position,
        emp_category: chosenUser?.emp_category,
        start_work_time: chosenUser?.start_work_time,
        end_work_time: chosenUser?.end_work_time,
      });
    }
  }, [chosenUser]);

  return (
    <Modal
      title={"Изменить данные сотрудника"}
      open={!!chosenUser}
      onCancel={onClose}
      confirmLoading={pending}
      footer={null}
    >
      <Form
        form={form}
        onFinish={onFinish}
        layout={"vertical"}
        style={{ marginTop: "24px" }}
      >
        {/* Photo Upload */}
        <Form.Item label="Фото сотрудника (максимум 300 KB)">
          <Upload
            name="photo"
            listType="picture"
            maxCount={1}
            beforeUpload={() => false}
            onChange={handlePhotoUpload}
            accept="image/*"
          >
            <Button icon={<UploadOutlined />}>Загрузить фото</Button>
          </Upload>
          {uploadedPhotoBase64 && (
            <div style={{ marginTop: 10 }}>
              <img
                src={uploadedPhotoBase64}
                alt="Preview"
                style={{
                  width: "100px",
                  height: "100px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                }}
              />
            </div>
          )}
        </Form.Item>

        <Form.Item name="user_name" label="Имя">
          <Input />
        </Form.Item>

        <Form.Item name="user_surname" label="Фамилия">
          <Input />
        </Form.Item>

        <Form.Item label="Пол" name="user_gender">
          <Select placeholder="Выберите пол">
            <Option value="Male">М</Option>
            <Option value="Female">Ж</Option>
          </Select>
        </Form.Item>

        <Form.Item name="emp_department" label="Департамент">
          <Select placeholder="Выберите департамент" allowClear>
            {[
              "Правление",
              "Юридический Департамент",
              "Отдел бухгалтерского учета и отчетности",
              "Финансово-экономический отдел",
              "Отдел маркетинга и PR",
              "Отдел по развитию человеческих ресурсов",
              "Группа контроля качества",
              "Административный отдел",
              "Департамент урегулирования убытков",
              "Департамент андеррайтинга и перестрахования",
              "Отдел по кадровому делопроизводству",
              "Отдел сопровождения и развития программного обеспечения",
              "Департамент экономической и внутренней безопасности",
              "Управление Информационной безопасности",
              "Группа Лояльности Клиентов",
              "Служба внутреннего аудита",
              "Исполнительный директор",
              "Дирекция продаж №10 по г. Алматы",
              "Дирекция продаж №1 по г.Алматы",
              "Дирекция продаж № 2 по г.Алматы",
              "Дирекция продаж №6 по г.Алматы",
              "Дирекция региональных продаж №7",
              "Дирекция продаж № 8 по г.Алматы",
              "Дирекция регионального развития",
              "ГО",
            ].map((dept) => (
              <Select.Option key={dept} value={dept}>
                {dept}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="emp_position" label="Должность">
          <Input />
        </Form.Item>

        <Form.Item name="emp_category" label="Статус">
          <Select options={selectOptions} />
        </Form.Item>

        <Form.Item
          name="start_work_time"
          label="Начало рабочего дня"
          rules={[
            {
              pattern: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
              message: "Используйте формат чч:мм (например 09:00)",
            },
          ]}
        >
          <Input placeholder="Введите время в формате чч:мм" />
        </Form.Item>

        <Form.Item
          name="end_work_time"
          label="Конец рабочего дня"
          rules={[
            {
              pattern: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
              message: "Используйте формат чч:мм (например 18:00)",
            },
          ]}
        >
          <Input placeholder="Введите время в формате чч:мм" />
        </Form.Item>
      </Form>

      <Button
        onClick={form.submit}
        className={style.modal_button}
        type="primary"
        style={{ marginRight: "8px" }}
      >
        Изменить
      </Button>
      <Button onClick={onClose} className={style.modal_button}>
        Отмена
      </Button>
    </Modal>
  );
}
